import { useQuery } from '@tanstack/react-query';
import {
  Award,
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  XCircle,
  AlertTriangle,
  ArrowLeft,
  Target,
  TrendingUp,
  AlertCircleIcon,
  ChevronDown,
  ChevronUp,
  Timer
} from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import apiClient from '@/services/api';
import { API_ENDPOINTS } from '@/config/api.config';
import { certificationService } from '@/services/certification.service';

// Types based on API response
enum ExamAttemptStatusEnum {
  IN_PROGRESS = 'IN_PROGRESS',
  SUBMITTED = 'SUBMITTED',
  COMPLETED = 'COMPLETED',
  ABANDONED = 'ABANDONED'
}

enum QuestionTypeEnum {
  SINGLE_CHOICE = 'SINGLE_CHOICE',
  MULTIPLE_CHOICE = 'MULTIPLE_CHOICE',
  TRUE_FALSE = 'TRUE_FALSE',
  TEXT_ANSWER = 'TEXT_ANSWER'
}

interface QuestionAttemptDto {
  answer_text: any;
  question_id: string;
  question_text: string;
  question_type: QuestionTypeEnum;
  answer_submitted: any;
  correct_answer?: string | string[] | null;
  options?: Array<{ option_id: string; option_text: string; is_correct?: boolean }>;
  is_correct: boolean;
  points_earned: number;
  max_points: number;
  time_spent_seconds?: number;
  submitted_at: string;
}

interface ExamAttemptDetailsDto {
  overall_time_spent_seconds: number;
  id: string;
  user_id: string;
  certification_id: string;
  certification_exam_id: string;
  attempt_number: number;
  status: ExamAttemptStatusEnum;
  started_at: string;
  submitted_at?: string;
  completed_at?: string;
  time_spent_seconds: number;
  question_attempts: QuestionAttemptDto[];
  score: number;
  max_score: number;
  percentage: number;
  passed: boolean;
  violation_count?: number;
  score_overridden?: boolean;
  original_score?: number;
  created_at: string;
  updated_at: string;
}

// Helper function to format time
const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
};

// Helper function to format date
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Learner's answer as display text, or null when not answered
const formatLearnerAnswer = (answer: any): string | null => {
  if (answer === undefined || answer === null) return null;
  if (typeof answer === 'string') return answer.trim() !== '' ? answer : null;
  if (Array.isArray(answer)) return answer.length > 0 ? answer.join(', ') : null;
  return JSON.stringify(answer);
};

// Option IDs the learner picked (single choice stores one ID, multiple choice an array)
const getSelectedOptionIds = (answer_submitted: any): string[] => {
  if (answer_submitted === undefined || answer_submitted === null || answer_submitted === '') return [];
  return Array.isArray(answer_submitted) ? answer_submitted.map(String) : [String(answer_submitted)];
};

// Status badge component
const StatusBadge = ({ status }: { status: ExamAttemptStatusEnum }) => {
  const statusConfig = {
    COMPLETED: { bg: 'bg-green-100', text: 'text-green-700', label: 'Completed' },
    SUBMITTED: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Submitted' },
    IN_PROGRESS: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'In Progress' },
    ABANDONED: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Abandoned' }
  };

  const config = statusConfig[status] || statusConfig.COMPLETED;

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

// Attempt Card Component
const AttemptCard = ({
  attempt,
  isExpanded,
  onToggle,
  showCorrectAnswersSetting = false
}: {
  attempt: ExamAttemptDetailsDto;
  isExpanded: boolean;
  onToggle: () => void;
  showCorrectAnswersSetting?: boolean;
}) => {
  const hasQuestions = attempt.question_attempts && attempt.question_attempts.length > 0;
  // Reveal only when the setting is on and the server actually sent correct answers for this attempt
  const showCorrectAnswers = showCorrectAnswersSetting && !!hasQuestions && attempt.question_attempts.some(q =>
    (q.correct_answer !== undefined && q.correct_answer !== null) || q.options?.some(o => o.is_correct !== undefined)
  );
  const correctAnswers = hasQuestions ? attempt.question_attempts.filter(q => q.is_correct).length : 0;
  const totalQuestions = hasQuestions ? attempt.question_attempts.length : 0;

  // Calculate actual score from question attempts for ABANDONED attempts
  // Backend sets score to 0 for abandoned attempts, but we want to show earned points
  const actualScore = attempt.status === ExamAttemptStatusEnum.ABANDONED && hasQuestions
    ? attempt.question_attempts.reduce((sum, q) => sum + (q.points_earned || 0), 0)
    : attempt.score;

  // Calculate actual percentage based on actual score
  const actualPercentage = attempt.max_score > 0
    ? (actualScore / attempt.max_score) * 100
    : 0;

  return (
    <div className="bg-white border border-gray-200 rounded-lg hover:shadow-md transition-all">
      {/* Attempt Header - Always Visible */}
      <div
        className="p-4 cursor-pointer"
        onClick={onToggle}
      >
        {/* Middle column is flex-1 min-w-0 so it takes the space left over and
            wraps inside it; badge and score never shrink. On a phone the status
            badge drops under the title and date/time wrap, instead of the whole
            row being crushed into a narrow column. */}
        <div className="flex items-start gap-3 sm:gap-4">
          {/* Attempt Number Circle */}
          <div className={`w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 rounded-full flex items-center justify-center font-bold text-base sm:text-lg ${
            attempt.passed
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700'
          }`}>
            #{attempt.attempt_number}
          </div>

          {/* Attempt Info */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 whitespace-nowrap">
                Attempt {attempt.attempt_number}
              </h3>
              <StatusBadge status={attempt.status} />
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                {formatDate(attempt.started_at)}
              </span>
              {attempt.overall_time_spent_seconds > 0 && (
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                  {formatTime(attempt.overall_time_spent_seconds)}
                </span>
              )}
            </div>
          </div>

          {/* Right Section - Score & Toggle */}
          <div className="flex items-start gap-1 sm:gap-4 flex-shrink-0">
            {/* Score Display */}
            <div className="text-right">
              <div className={`text-2xl sm:text-3xl font-bold leading-tight ${
                attempt.passed ? 'text-green-600' : 'text-red-600'
              }`}>
                {Math.round(actualPercentage)}%
              </div>
              <div className="text-xs sm:text-sm text-gray-600 whitespace-nowrap">
                {actualScore}/{attempt.max_score} <span className="sm:hidden">pts</span><span className="hidden sm:inline">points</span>
              </div>
            </div>

            {/* Toggle Icon */}
            {hasQuestions && (
              <button className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-full transition-colors">
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5 text-gray-600" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-600" />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Stats Row */}
        <div className="mt-3 pt-3 border-t border-gray-100 sm:mt-4 sm:pt-0 sm:border-0 flex flex-wrap items-center gap-x-4 gap-y-2 sm:grid sm:grid-cols-3 sm:gap-4">
          <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm">
            <Target className="w-4 h-4 text-gray-500" />
            <span className="text-gray-600">Correct:</span>
            <span className="font-semibold text-gray-900">{correctAnswers}/{totalQuestions}</span>
          </div>
          {attempt.violation_count !== undefined && (
            <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <span className="text-gray-600">Violations:</span>
              <span className="font-semibold text-gray-900">{attempt.violation_count}</span>
            </div>
          )}
          <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm ml-auto sm:ml-0">
            {attempt.passed ? (
              <>
                <Award className="w-4 h-4 text-green-500" />
                <span className="font-semibold text-green-600">Passed</span>
              </>
            ) : (
              <>
                <XCircle className="w-4 h-4 text-red-500" />
                <span className="font-semibold text-red-600">Failed</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Section - Questions */}
      {isExpanded && (
        <div className="border-t border-gray-200">
          {!hasQuestions ? (
            <div className="p-8 text-center">
              {attempt.status === ExamAttemptStatusEnum.ABANDONED ? (
                <>
                  <XCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                  <p className="text-amber-700 font-medium">This attempt has been abandoned</p>
                  <p className="text-sm text-gray-500 mt-1">No questions were answered before abandoning.</p>
                </>
              ) : (
                <>
                  <AlertCircleIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium">Not Yet Answered</p>
                  <p className="text-sm text-gray-500 mt-1">No questions have been answered in this attempt.</p>
                </>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {attempt.question_attempts.map((question, index) => (
                <div
                  key={question.question_id}
                  className={`p-4 ${question.is_correct ? 'bg-green-50/30' : 'bg-red-50/30'}`}
                >
                  <div className="flex gap-3">
                    {/* Question Number */}
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      question.is_correct ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {index + 1}
                    </div>

                    {/* Question Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <h4 className="text-sm sm:text-base text-gray-900 font-medium flex-1 min-w-0 break-words">
                          {question.question_text}
                        </h4>

                        {/* Points Badge */}
                        <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium flex-shrink-0 ${
                          question.is_correct
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {question.is_correct ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <XCircle className="w-4 h-4" />
                          )}
                          <span>{question.points_earned}/{question.max_points}</span>
                        </div>
                      </div>

                      {/* Question Meta */}
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600 mb-3">
                        <span className="flex items-center gap-1">
                          <FileText className="w-3.5 h-3.5" />
                          {question.question_type.replace(/_/g, ' ')}
                        </span>
                        {question.time_spent_seconds && (
                          <span className="flex items-center gap-1">
                            <Timer className="w-3.5 h-3.5" />
                            {formatTime(question.time_spent_seconds)}
                          </span>
                        )}
                      </div>

                      {showCorrectAnswers && question.options && question.options.length > 0 ? (
                        // Correct answers on: every option, with the learner's pick tagged
                        <div className="space-y-1.5">
                          {getSelectedOptionIds(question.answer_submitted).length === 0 && (
                            <div className="p-2.5 rounded-md text-xs bg-yellow-50 border border-yellow-200">
                              <span className="font-medium text-yellow-700">Not Answered</span>
                            </div>
                          )}
                          {question.options.map((option) => {
                            const is_selected = getSelectedOptionIds(question.answer_submitted).includes(option.option_id);
                            const option_class = !is_selected
                              ? 'bg-gray-50 border-gray-200'
                              : option.is_correct
                              ? 'bg-green-50 border-green-200'
                              : 'bg-red-50 border-red-200';

                            return (
                              <div key={option.option_id} className={`p-2.5 rounded-md text-sm border ${option_class}`}>
                                <div className="flex items-center gap-2">
                                  {is_selected && (
                                    <span className="text-xs font-medium text-gray-700 px-1.5 py-0.5 bg-white rounded border border-gray-200">
                                      Your answer
                                    </span>
                                  )}
                                  <span className="text-gray-900">{option.option_text}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        // Correct answers off, or text answers: only the learner's answer
                        <div className={`rounded-lg p-3 border ${
                          formatLearnerAnswer(question.answer_text)
                            ? question.is_correct ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                            : 'bg-white border-gray-200'
                        }`}>
                          <p className="text-xs font-medium text-gray-700 mb-1.5">Your Answer:</p>
                          <p className="text-sm text-gray-900">
                            {formatLearnerAnswer(question.answer_text) ?? 'Not Answered'}
                          </p>
                        </div>
                      )}

                      {/* Correct Answer: for wrong answers, and always for text answers (expected keywords) */}
                      {showCorrectAnswers && question.correct_answer !== undefined && question.correct_answer !== null &&
                        (!question.is_correct || question.question_type === QuestionTypeEnum.TEXT_ANSWER) && (
                        <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-200">
                          <p className="text-xs font-semibold text-gray-700 mb-1.5">Correct Answer:</p>
                          {Array.isArray(question.correct_answer) ? (
                            question.question_type === QuestionTypeEnum.TEXT_ANSWER ? (
                              <>
                                <p className="text-xs text-gray-600 mb-1">Expected keywords/criteria:</p>
                                <ul className="list-disc list-inside space-y-1">
                                  {question.correct_answer.map((item, idx) => (
                                    <li key={idx} className="text-sm text-green-700 font-medium">{item}</li>
                                  ))}
                                </ul>
                              </>
                            ) : (
                              <p className="text-sm text-green-700 font-medium">{question.correct_answer.join(', ')}</p>
                            )
                          ) : (
                            <p className="text-sm text-green-700 font-medium">{question.correct_answer}</p>
                          )}
                        </div>
                      )}

                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function LearnerExamResults() {
  const navigate = useNavigate();
  const location = useLocation();
  const { examId } = useParams<{ examId: string }>();
  const [expandedAttempts, setExpandedAttempts] = useState<Set<string>>(new Set());

  // Get show_correct_answers from navigation state (hidden unless explicitly enabled)
  const showCorrectAnswersFromState = location.state?.show_correct_answers;

  // Fetch certification details to get show_correct_answers flag
  const { data: certificationData, isLoading: isCertificationLoading } = useQuery({
    queryKey: ['certification-details', examId],
    queryFn: async () => {
      if (!examId) return null;
      try {
        const certifications = await certificationService.getUserCertifications();
        return certifications.find(c => c.certification_id === examId);
      } catch (error) {
        console.error('Failed to fetch certification details:', error);
        return null;
      }
    },
    enabled: !!examId
  });

  // Correct answers stay hidden unless the setting is explicitly true
  const showCorrectAnswers = showCorrectAnswersFromState ?? certificationData?.show_correct_answers === true;

  // The list hides "View Result" when show_feedback is off; this also covers opening the URL directly
  const resultsDisabled = certificationData?.show_feedback === false;

  useEffect(() => {
    if (!resultsDisabled) return;
    toast.error('The admin has disabled the results section. Please contact your admin to enable it.', {
      id: 'results-disabled'
    });
    navigate('/learn/certifications', { replace: true });
  }, [resultsDisabled, navigate]);

  // Fetch exam attempts
  const { data: attempts, isLoading } = useQuery({
    queryKey: ['exam-attempts', examId],
    queryFn: async () => {
      if (!examId) {
        throw new Error('Exam ID is required');
      }

      try {
        const response = await apiClient.get(
          API_ENDPOINTS.CERTIFICATIONS.LIST_ATTEMPTS(examId)
        );

        if (response.data.success) {
          console.log('📊 Exam Attempts:', response.data.data);
          return response.data.data as ExamAttemptDetailsDto[];
        }

        throw new Error('Failed to fetch exam attempts');
      } catch (error: any) {
        console.error('Failed to fetch exam attempts:', error);
        toast.error(error.response?.data?.message || 'Failed to load exam results');
        throw error;
      }
    },
    // Wait for the settings so results are never fetched or shown when feedback is disabled
    enabled: !!examId && !isCertificationLoading && !resultsDisabled
  });

  const toggleAttempt = (attemptId: string) => {
    setExpandedAttempts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(attemptId)) {
        newSet.delete(attemptId);
      } else {
        newSet.add(attemptId);
      }
      return newSet;
    });
  };

  // Redirecting away (see effect above)
  if (resultsDisabled) {
    return null;
  }

  if (isLoading || isCertificationLoading) {
    return (
      <div className="space-y-4 md:space-y-6">
        <div className="bg-white rounded-lg border border-gray-200 p-16 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-amber-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading exam results...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!attempts || attempts.length === 0) {
    return (
      <div className="space-y-4 md:space-y-6">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-brand-blue-600 to-brand-indigo-600 rounded-xl p-4 md:p-6 text-white">
          {/* Back Button */}
          <button
            onClick={() => navigate('/learn/certifications')}
            className="flex items-center gap-2 text-white/90 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Certifications</span>
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold mb-2 flex items-center gap-2">
                <Award className="w-6 h-6 md:w-7 md:h-7" />
                Exam Results
              </h1>
              <p className="text-blue-100 text-sm md:text-base">
                View your exam attempt history and performance
              </p>
            </div>
          </div>
        </div>

        {/* Empty State */}
        <div className="bg-white rounded-lg border border-gray-200 p-8 md:p-16 text-center">
          <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">No Results Found</h2>
          <p className="text-gray-600 mb-6">
            You haven't completed any exam attempts yet.
          </p>
        </div>
      </div>
    );
  }

  // Calculate overall stats
  const totalAttempts = attempts.length;
  const passedAttempts = attempts.filter(a => a.passed).length;
  const bestScore = Math.max(...attempts.map(a => a.percentage));
  const averageScore = attempts.reduce((sum, a) => sum + a.percentage, 0) / totalAttempts;
  const totalTimeSpent = attempts.reduce((sum, a) => sum + a.overall_time_spent_seconds, 0);

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header with gradient and stats */}
      <div className="bg-gradient-to-r from-brand-blue-600 to-brand-indigo-600 rounded-xl p-4 md:p-6 text-white">
        {/* Back Button */}
        <button
          onClick={() => navigate('/learn/certifications')}
          className="flex items-center gap-2 text-white/90 hover:text-white transition-colors mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Back to Certifications</span>
        </button>

        <div className="flex items-center justify-between mb-4 md:mb-6">
          <div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold mb-2 flex items-center gap-2">
              <Award className="w-6 h-6 md:w-7 md:h-7" />
              Exam Results
            </h1>
            <p className="text-blue-100 text-sm md:text-base">
              View your exam attempt history and performance
            </p>
          </div>
        </div>

        {/* Stats Cards - matching certifications page style */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-blue-100 text-xs md:text-sm">Total Attempts</p>
            <p className="text-xl md:text-2xl font-bold">{totalAttempts}</p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-blue-100 text-xs md:text-sm">Passed</p>
            <p className="text-xl md:text-2xl font-bold">{passedAttempts}</p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-blue-100 text-xs md:text-sm">Best Score</p>
            <p className="text-xl md:text-2xl font-bold">{Math.round(bestScore)}%</p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-blue-100 text-xs md:text-sm">Total Time</p>
            <p className="text-xl md:text-2xl font-bold">{formatTime(totalTimeSpent)}</p>
          </div>
        </div>
      </div>

      {/* Attempts List */}
      <div className="space-y-3 md:space-y-4">
        {attempts.map((attempt) => (
          <AttemptCard
            key={attempt.id}
            attempt={attempt}
            isExpanded={expandedAttempts.has(attempt.id)}
            onToggle={() => toggleAttempt(attempt.id)}
            showCorrectAnswersSetting={showCorrectAnswers}
          />
        ))}
      </div>
    </div>
  );
}
